/**
 * CoPilotEngineProvider (journey-web) — hoists the ONE Companion engine to the
 * app root (0089).
 *
 * Before this, `useCoPilotBrainCore` was called INSIDE `CoPilotBrain`, which
 * lives in the per-route `AppShellLayout`. Navigating hero→hero remounted that
 * layout, re-initialised the engine, and lost the thread — the reset bug (a
 * conversation that "sometimes kept, sometimes reset"). Mounting the engine once
 * here, above the router, means navigation can never unmount it: the thread (and
 * `currentConversationId`) survives close → navigate → reopen.
 *
 * `CoPilotBrain` and the history panel now read the engine from this context
 * instead of calling the hook.
 */

import { useCoPilotBrainCore, type IUseCoPilotBrainReturn } from '@tonnie/copilot-core';
import { browserStorage } from '@tonnie/copilot-ui';
import { useTrackEvent } from '@tonnie/sdk';
import { createContext, useCallback, useContext, useEffect, type ReactNode } from 'react';

import { useCoPilotSidebar } from '@/core/contexts/CoPilotSidebarContext';
import { useAuth } from '@/core/contexts/AuthContext';
import { useCoPilotContext } from '@/core/contexts/CoPilotContextProvider';
import { useJourneyInstance } from '@/core/contexts/JourneyInstanceContext';

/**
 * Citizen actions that CONSUME the composer's attachment — a successful confirm
 * clears the pending file. (The Protector has its own document push.)
 */
const CITIZEN_FILE_ACTIONS = ['attach_file_to_teleportal_task', 'save_file_to_vault'] as const;

const CoPilotEngineContext = createContext<IUseCoPilotBrainReturn | null>(null);

export function CoPilotEngineProvider({ children }: { children: ReactNode }) {
  const { isOpen } = useCoPilotSidebar();
  const { pageContext, flushPendingEvents, askedQuestion, takeAskedQuestion } = useCoPilotContext();
  const { user } = useAuth();
  const trackEvent = useTrackEvent();

  // Resolved per turn so the Companion always sees the CURRENT page (pageContext
  // is app-root + live, so it updates as the user navigates — no per-route prop).
  // …and the journey the citizen is looking at, so the Companion grounds and
  // acts on THAT journey, not merely the primary one (X6).
  const { activeInstance } = useJourneyInstance();
  const journeyInstanceId = activeInstance?.id;
  const resolveContext = useCallback(
    () => ({
      hero: pageContext.hero ?? undefined,
      screen: pageContext.screen,
      portal: pageContext.portal,
      journeyInstanceId,
    }),
    [pageContext, journeyInstanceId]
  );

  const brain = useCoPilotBrainCore(
    { hero: pageContext.hero ?? undefined, screen: pageContext.screen },
    isOpen,
    {
      resolveContext,
      flushPendingEvents,
      userId: user?.id,
      trackEvent,
      storage: browserStorage,
      fileActionIds: CITIZEN_FILE_ACTIONS,
    }
  );

  // A question the page asked (`useAskCoPilot`) goes to the engine, which sends
  // it once the conversation is read.
  const { ask } = brain;
  useEffect(() => {
    if (askedQuestion === null) return;
    ask(askedQuestion);
    takeAskedQuestion();
  }, [askedQuestion, ask, takeAskedQuestion]);

  return <CoPilotEngineContext.Provider value={brain}>{children}</CoPilotEngineContext.Provider>;
}

/** Read the app-root Companion engine. Throws if used outside the provider. */
export function useCoPilotEngine(): IUseCoPilotBrainReturn {
  const ctx = useContext(CoPilotEngineContext);
  if (!ctx) {
    throw new Error('useCoPilotEngine must be used within <CoPilotEngineProvider>');
  }
  return ctx;
}
